import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  MessageCircle,
  Plus,
  Search,
  Phone,
  Mail,
  PawPrint,
  CheckCircle2,
  Save,
  RefreshCw,
  Bot,
  UserRound,
  Send,
  X,
} from "lucide-react";
import "../static/leads.css";

const emptyNewLead = {
  phone: "",
  whatsapp_consent: false,
};

const Leads = () => {
  const [leads, setLeads] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);

  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [messageLoading, setMessageLoading] = useState(false);

  const [newOpen, setNewOpen] = useState(false);
  const [newLead, setNewLead] = useState(emptyNewLead);

  const [messageText, setMessageText] = useState("");
  const [notice, setNotice] = useState("");

  const loadLeads = async (preferredId = null) => {
    try {
      setLoading(true);

      const res = await axios.get("/petgrooming/leads/");
      const rows = res.data?.results || [];

      setLeads(rows);

      const targetId =
        preferredId ||
        selectedId ||
        (rows.length ? rows[0].id : null);

      if (targetId) {
        setSelectedId(targetId);

        const existing = rows.find(
          (item) => Number(item.id) === Number(targetId)
        );

        if (existing) {
          setSelected(existing);
        }
      } else {
        setSelectedId(null);
        setSelected(null);
      }
    } catch (error) {
      console.error(error);
      setNotice("Could not load leads.");
    } finally {
      setLoading(false);
    }
  };

  const loadLead = async (leadId) => {
    if (!leadId) return;

    try {
      const res = await axios.get(
        `/petgrooming/leads/${leadId}/`
      );

      setSelected(res.data?.lead || null);
    } catch (error) {
      console.error(error);
      setNotice("Could not load lead.");
    }
  };

  const loadMessages = async (leadId) => {
    if (!leadId) {
      setMessages([]);
      return;
    }

    try {
      setMessageLoading(true);

      const res = await axios.get(
        `/petgrooming/leads/${leadId}/messages/`
      );

      setMessages(res.data?.results || []);
    } catch (error) {
      console.error(error);
      setMessages([]);
    } finally {
      setMessageLoading(false);
    }
  };

  useEffect(() => {
    loadLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedId) return;

    loadLead(selectedId);
    loadMessages(selectedId);
  }, [selectedId]);

  const filteredLeads = useMemo(() => {
    const q = search.trim().toLowerCase();

    if (!q) return leads;

    return leads.filter((lead) => {
      const haystack = [
        lead.name,
        lead.phone,
        lead.email,
        lead.pet_name,
        lead.pet_type,
        lead.breed,
        lead.service,
        lead.source,
        lead.status,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(q);
    });
  }, [leads, search]);

  const updateSelected = (field, value) => {
    setSelected((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const saveSelected = async () => {
    if (!selected?.id) return;

    try {
      setSaving(true);
      setNotice("");

      const payload = {
        name: selected.name || "",
        phone: selected.phone || "",
        email: selected.email || "",
        source: selected.source || "other",
        whatsapp_consent: Boolean(
          selected.whatsapp_consent
        ),
        pet_name: selected.pet_name || "",
        pet_type: selected.pet_type || "",
        breed: selected.breed || "",
        service: selected.service || "",
        status: selected.status || "new",
        ai_enabled: Boolean(selected.ai_enabled),
        notes: selected.notes || "",
      };

      const res = await axios.patch(
        `/petgrooming/leads/${selected.id}/`,
        payload
      );

      const saved = res.data?.lead;

      if (saved) {
        setSelected(saved);
        await loadLeads(saved.id);
      }

      setNotice("Lead saved.");
    } catch (error) {
      console.error(error);
      setNotice(
        error.response?.data?.error ||
          "Could not save lead."
      );
    } finally {
      setSaving(false);
    }
  };

  const startWhatsApp = async (event) => {
    event.preventDefault();

    if (!newLead.phone.trim()) {
      setNotice("Please enter a phone number.");
      return;
    }

    if (!newLead.whatsapp_consent) {
      setNotice(
        "Please confirm WhatsApp consent."
      );
      return;
    }

    try {
      setSaving(true);
      setNotice("");

      const res = await axios.post(
        "/petgrooming/whatsapp/start/",
        {
          phone: newLead.phone.trim(),
          whatsapp_consent: true,
        }
      );

      const leadId = res.data?.lead_id;

      if (!leadId) {
        throw new Error(
          "Customer record was not returned."
        );
      }

      setNewLead(emptyNewLead);
      setNewOpen(false);

      await loadLeads(leadId);
      setSelectedId(leadId);

      setNotice(
        "WhatsApp conversation started."
      );
    } catch (error) {
      console.error(error);

      setNotice(
        error.response?.data?.error ||
          "Could not start WhatsApp conversation."
      );
    } finally {
      setSaving(false);
    }
  };

  const syncWebsite = async () => {
    try {
      setLoading(true);
      setNotice("");

      const res = await axios.post(
        "/petgrooming/leads/sync-website/"
      );

      await loadLeads();

      setNotice(
        `${res.data?.created || 0} new website lead(s) imported.`
      );
    } catch (error) {
      console.error(error);
      setNotice("Could not sync website leads.");
    } finally {
      setLoading(false);
    }
  };

  const saveMessage = async (event) => {
    event.preventDefault();

    const text = messageText.trim();

    if (!selectedId || !text) return;

    try {
      setMessageLoading(true);
      setNotice("");

      await axios.post(
        `/petgrooming/whatsapp/send/${selectedId}/`,
        { text }
      );

      setMessageText("");

      await loadMessages(selectedId);
      await loadLeads(selectedId);

      setNotice(
        "WhatsApp message sent."
      );
    } catch (error) {
      console.error(error);

      setNotice(
        error.response?.data?.error ||
          "Could not send WhatsApp message."
      );
    } finally {
      setMessageLoading(false);
    }
  };

  const statusLabel = (status) => {
    const map = {
      new: "New",
      chatting: "Chatting",
      interested: "Interested",
      booked: "Booked",
      no_response: "No response",
      closed: "Closed",
    };

    return map[status] || status || "New";
  };

  return (
    <main className="lead-page">
      <section className="lead-topbar">
        <div>
          <span className="lead-eyebrow">
            Customer conversations
          </span>

          <h1>ARYS Leads</h1>

          <p>
            Phone enquiries, website customers and future
            WhatsApp conversations in one place.
          </p>
        </div>

        <div className="lead-top-actions">
          <button
            type="button"
            className="lead-btn secondary"
            onClick={syncWebsite}
          >
            <RefreshCw size={17} />
            Sync website
          </button>

          <button
            type="button"
            className="lead-btn primary"
            onClick={() => setNewOpen(true)}
          >
            <Plus size={18} />
            Start WhatsApp
          </button>
        </div>
      </section>

      {notice && (
        <div className="lead-notice">
          <CheckCircle2 size={18} />
          <span>{notice}</span>

          <button
            type="button"
            onClick={() => setNotice("")}
          >
            <X size={16} />
          </button>
        </div>
      )}

      <section className="lead-layout">
        <aside className="lead-sidebar">
          <div className="lead-search">
            <Search size={18} />
            <input
              value={search}
              onChange={(event) =>
                setSearch(event.target.value)
              }
              placeholder="Search name, phone, pet..."
            />
          </div>

          <div className="lead-count">
            {filteredLeads.length} lead
            {filteredLeads.length === 1 ? "" : "s"}
          </div>

          <div className="lead-list">
            {loading ? (
              <div className="lead-empty">
                Loading leads...
              </div>
            ) : filteredLeads.length ? (
              filteredLeads.map((lead) => (
                <button
                  key={lead.id}
                  type="button"
                  className={
                    Number(selectedId) === Number(lead.id)
                      ? "lead-list-item active"
                      : "lead-list-item"
                  }
                  onClick={() =>
                    setSelectedId(lead.id)
                  }
                >
                  <span className="lead-avatar">
                    {(lead.name || lead.phone || "?")
                      .trim()
                      .charAt(0)
                      .toUpperCase()}
                  </span>

                  <span className="lead-list-main">
                    <strong>
                      {lead.name || "Unnamed customer"}
                    </strong>

                    <small>
                      {lead.phone ||
                        lead.email ||
                        "No contact"}
                    </small>

                    <span className="lead-list-meta">
                      {lead.pet_name && (
                        <span>
                          <PawPrint size={12} />
                          {lead.pet_name}
                        </span>
                      )}

                      <span
                        className={`lead-status ${lead.status}`}
                      >
                        {statusLabel(lead.status)}
                      </span>
                    </span>
                  </span>
                </button>
              ))
            ) : (
              <div className="lead-empty">
                No leads yet.
              </div>
            )}
          </div>
        </aside>

        <section className="lead-content">
          {!selected ? (
            <div className="lead-welcome-empty">
              <MessageCircle size={42} />

              <h2>Select a lead</h2>

              <p>
                Choose a customer from the left or create
                a new lead.
              </p>
            </div>
          ) : (
            <>
              <div className="lead-profile-head">
                <div className="lead-profile-person">
                  <span className="lead-profile-avatar">
                    {(selected.name ||
                      selected.phone ||
                      "?")
                      .trim()
                      .charAt(0)
                      .toUpperCase()}
                  </span>

                  <div>
                    <span className="lead-eyebrow">
                      {selected.source || "lead"}
                    </span>

                    <h2>
                      {selected.name ||
                        "Unnamed customer"}
                    </h2>

                    <div className="lead-contact-line">
                      {selected.phone && (
                        <span>
                          <Phone size={14} />
                          {selected.phone}
                        </span>
                      )}

                      {selected.email && (
                        <span>
                          <Mail size={14} />
                          {selected.email}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  className="lead-btn primary"
                  onClick={saveSelected}
                  disabled={saving}
                >
                  <Save size={17} />
                  {saving ? "Saving..." : "Save"}
                </button>
              </div>

              <div className="lead-detail-grid">
                <section className="lead-card">
                  <div className="lead-card-title">
                    <UserRound size={18} />
                    <div>
                      <small>Contact</small>
                      <h3>Customer details</h3>
                    </div>
                  </div>

                  <div className="lead-form-grid">
                    <label>
                      Name
                      <input
                        value={selected.name || ""}
                        onChange={(e) =>
                          updateSelected(
                            "name",
                            e.target.value
                          )
                        }
                      />
                    </label>

                    <label>
                      Phone / WhatsApp
                      <input
                        value={selected.phone || ""}
                        onChange={(e) =>
                          updateSelected(
                            "phone",
                            e.target.value
                          )
                        }
                      />
                    </label>

                    <label>
                      Email
                      <input
                        type="email"
                        value={selected.email || ""}
                        onChange={(e) =>
                          updateSelected(
                            "email",
                            e.target.value
                          )
                        }
                      />
                    </label>

                    <label>
                      Source
                      <select
                        value={selected.source || "other"}
                        onChange={(e) =>
                          updateSelected(
                            "source",
                            e.target.value
                          )
                        }
                      >
                        <option value="phone">
                          Phone Call
                        </option>
                        <option value="website">
                          Website
                        </option>
                        <option value="whatsapp">
                          WhatsApp
                        </option>
                        <option value="instagram">
                          Instagram
                        </option>
                        <option value="email">
                          Email
                        </option>
                        <option value="other">
                          Other
                        </option>
                      </select>
                    </label>
                  </div>

                  <label className="lead-checkbox">
                    <input
                      type="checkbox"
                      checked={Boolean(
                        selected.whatsapp_consent
                      )}
                      onChange={(e) =>
                        updateSelected(
                          "whatsapp_consent",
                          e.target.checked
                        )
                      }
                    />

                    <span>
                      Customer agreed to WhatsApp contact
                    </span>
                  </label>
                </section>

                <section className="lead-card">
                  <div className="lead-card-title">
                    <PawPrint size={18} />
                    <div>
                      <small>Pet</small>
                      <h3>Pet & service</h3>
                    </div>
                  </div>

                  <div className="lead-form-grid">
                    <label>
                      Pet name
                      <input
                        value={selected.pet_name || ""}
                        onChange={(e) =>
                          updateSelected(
                            "pet_name",
                            e.target.value
                          )
                        }
                      />
                    </label>

                    <label>
                      Pet type
                      <select
                        value={selected.pet_type || ""}
                        onChange={(e) =>
                          updateSelected(
                            "pet_type",
                            e.target.value
                          )
                        }
                      >
                        <option value="">
                          Not known
                        </option>
                        <option value="Cat">Cat</option>
                        <option value="Dog">Dog</option>
                      </select>
                    </label>

                    <label>
                      Breed
                      <input
                        value={selected.breed || ""}
                        onChange={(e) =>
                          updateSelected(
                            "breed",
                            e.target.value
                          )
                        }
                      />
                    </label>

                    <label>
                      Service
                      <input
                        value={selected.service || ""}
                        onChange={(e) =>
                          updateSelected(
                            "service",
                            e.target.value
                          )
                        }
                      />
                    </label>
                  </div>
                </section>

                <section className="lead-card">
                  <div className="lead-card-title">
                    <Bot size={18} />
                    <div>
                      <small>Automation</small>
                      <h3>Lead status</h3>
                    </div>
                  </div>

                  <div className="lead-form-grid">
                    <label>
                      Status
                      <select
                        value={selected.status || "new"}
                        onChange={(e) =>
                          updateSelected(
                            "status",
                            e.target.value
                          )
                        }
                      >
                        <option value="new">New</option>
                        <option value="chatting">
                          Chatting
                        </option>
                        <option value="interested">
                          Interested
                        </option>
                        <option value="booked">
                          Booked
                        </option>
                        <option value="no_response">
                          No response
                        </option>
                        <option value="closed">
                          Closed
                        </option>
                      </select>
                    </label>

                    <label className="lead-switch-row">
                      <span>
                        <strong>AI Assistant</strong>
                        <small>
                          Will be connected after WhatsApp
                        </small>
                      </span>

                      <input
                        type="checkbox"
                        checked={Boolean(
                          selected.ai_enabled
                        )}
                        onChange={(e) =>
                          updateSelected(
                            "ai_enabled",
                            e.target.checked
                          )
                        }
                      />
                    </label>
                  </div>

                  <label>
                    Notes
                    <textarea
                      rows="4"
                      value={selected.notes || ""}
                      onChange={(e) =>
                        updateSelected(
                          "notes",
                          e.target.value
                        )
                      }
                      placeholder="Internal notes..."
                    />
                  </label>
                </section>

                <section className="lead-card conversation-card">
                  <div className="lead-card-title">
                    <MessageCircle size={18} />
                    <div>
                      <small>Conversation</small>
                      <h3>Messages</h3>
                    </div>
                  </div>

                  <div className="lead-chat">
                    {messageLoading &&
                    !messages.length ? (
                      <div className="lead-empty">
                        Loading conversation...
                      </div>
                    ) : messages.length ? (
                      messages.map((message) => (
                        <div
                          key={message.id}
                          className={`lead-message ${message.direction}`}
                        >
                          <div>
                            <strong>
                              {message.direction ===
                              "inbound"
                                ? selected.name ||
                                  "Customer"
                                : message.sender ||
                                  "ARYS"}
                            </strong>

                            <span>
                              {message.channel}
                            </span>
                          </div>

                          <p>{message.text}</p>

                          <small>
                            {message.created_at
                              ? new Date(
                                  message.created_at
                                ).toLocaleString(
                                  "en-GB"
                                )
                              : ""}
                          </small>
                        </div>
                      ))
                    ) : (
                      <div className="lead-empty">
                        No conversation yet.
                      </div>
                    )}
                  </div>

                  <form
                    className="lead-message-form"
                    onSubmit={saveMessage}
                  >
                    <textarea
                      value={messageText}
                      onChange={(e) =>
                        setMessageText(e.target.value)
                      }
                      rows="3"
                      placeholder="Write a message..."
                    />

                    <div className="lead-message-footer">
                      <small>
                        Phase 1: saved internally only.
                        WhatsApp connection comes next.
                      </small>

                      <button
                        type="submit"
                        className="lead-btn primary"
                        disabled={
                          messageLoading ||
                          !messageText.trim()
                        }
                      >
                        <Send size={17} />
                        Save message
                      </button>
                    </div>
                  </form>
                </section>
              </div>
            </>
          )}
        </section>
      </section>

      {newOpen && (
        <div
          className="lead-modal-backdrop"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              setNewOpen(false);
            }
          }}
        >
          <form
            className="lead-modal"
            onSubmit={startWhatsApp}
          >
            <div className="lead-modal-head">
              <div>
                <span className="lead-eyebrow">
                  WhatsApp
                </span>
                <h2>Start conversation</h2>
              </div>

              <button
                type="button"
                onClick={() => setNewOpen(false)}
              >
                <X size={20} />
              </button>
            </div>

            <label>
              Phone number
              <input
                autoFocus
                value={newLead.phone}
                onChange={(e) =>
                  setNewLead({
                    ...newLead,
                    phone: e.target.value,
                  })
                }
                placeholder="07..."
              />
            </label>

            <label className="lead-checkbox">
              <input
                type="checkbox"
                checked={
                  newLead.whatsapp_consent
                }
                onChange={(e) =>
                  setNewLead({
                    ...newLead,
                    whatsapp_consent:
                      e.target.checked,
                  })
                }
              />

              <span>
                Customer agreed to WhatsApp contact
              </span>
            </label>

            <div className="lead-modal-actions">
              <button
                type="button"
                className="lead-btn secondary"
                onClick={() => setNewOpen(false)}
              >
                Cancel
              </button>

              <button
                type="submit"
                className="lead-btn primary"
                disabled={saving}
              >
                <MessageCircle size={17} />
                {saving
                  ? "Starting..."
                  : "Start WhatsApp"}
              </button>
            </div>
          </form>
        </div>
      )}
    </main>
  );
};

export default Leads;
